import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
  Backdrop
} from '@mui/material';
import { ArrowBack, Search, Add, Edit, Delete, CheckCircle, Cancel, Sync, Close } from '@mui/icons-material';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { log } from '../services/loggingService';
import { syncCategories } from '../services/productService';

function TechyShopCategorie() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Category form state
  const [categoryName, setCategoryName] = useState('');
  const [slug, setSlug] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [immagine, setImmagine] = useState('');
  const [categoriaPadreId, setCategoriaPadreId] = useState(0);
  const [visibile, setVisibile] = useState(true);
  const [ordinamento, setOrdinamento] = useState(0);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Ottieni categorie in tempo reale dal DB
  const categories = useLiveQuery(() => db.techyshop_categorie.toArray(), []);

  // Memorizza le categorie filtrate
  const filteredCategories = useMemo(() => {
    if (!categories) return [];
    
    const lowercasedQuery = searchQuery.toLowerCase();
    
    return categories
      .filter(cat => {
        const nome = cat.nome || '';
        const id = cat.id ? cat.id.toString() : '';
        const catSlug = cat.slug || '';
        
        return id.includes(lowercasedQuery) ||
               nome.toLowerCase().includes(lowercasedQuery) ||
               catSlug.toLowerCase().includes(lowercasedQuery);
      });
  }, [searchQuery, categories]);

  const handleOpenModal = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setCategoryName(category.nome || '');
      setSlug(category.slug || '');
      setDescrizione(category.descrizione || '');
      setImmagine(category.immagine || '');
      setCategoriaPadreId(category.categoria_padre_id || 0);
      setVisibile(category.visibile == 1 || String(category.visibile).toLowerCase() === 'true');
      setOrdinamento(category.ordinamento || 0);
      setMetaTitle(category.meta_title || '');
      setMetaDescription(category.meta_description || '');
    } else {
      setSelectedCategory(null);
      setCategoryName('');
      setSlug('');
      setDescrizione('');
      setImmagine('');
      setCategoriaPadreId(0);
      setVisibile(true);
      setOrdinamento(0);
      setMetaTitle('');
      setMetaDescription('');
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCategory(null);
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim() || !slug.trim()) {
      setToast({ open: true, message: 'Errore: Nome e Slug sono campi obbligatori.', severity: 'error' });
      return;
    }

    setIsSaving(true);
    
    const categoryData = {
      nome: categoryName,
      slug: slug,
      descrizione: descrizione,
      immagine: immagine,
      categoria_padre_id: categoriaPadreId,
      visibile: visibile ? 1 : 0,
      ordinamento: parseInt(ordinamento) || 0,
      meta_title: metaTitle,
      meta_description: metaDescription
    };

    try {
      if (selectedCategory) {
        // Logica di modifica (stile attributi)
        const values = [];
        
        if (categoryName !== selectedCategory.nome) values.push({ field: 'nome', value: categoryName });
        if (slug !== selectedCategory.slug) values.push({ field: 'slug', value: slug });
        if (descrizione !== selectedCategory.descrizione) values.push({ field: 'descrizione', value: descrizione });
        if (immagine !== selectedCategory.immagine) values.push({ field: 'immagine', value: immagine });
        if (categoriaPadreId !== selectedCategory.categoria_padre_id) values.push({ field: 'categoria_padre_id', value: categoriaPadreId });
        
        const visibileValue = visibile ? 1 : 0;
        const currentVisibileValue = (selectedCategory.visibile == 1 || String(selectedCategory.visibile).toLowerCase() === 'true') ? 1 : 0;
        if (visibileValue !== currentVisibileValue) values.push({ field: 'visibile', value: visibileValue });
        
        if (parseInt(ordinamento) !== (selectedCategory.ordinamento || 0)) values.push({ field: 'ordinamento', value: parseInt(ordinamento) || 0 });
        if (metaTitle !== (selectedCategory.meta_title || '')) values.push({ field: 'meta_title', value: metaTitle });
        if (metaDescription !== (selectedCategory.meta_description || '')) values.push({ field: 'meta_description', value: metaDescription });

        if (values.length === 0) {
          handleCloseModal();
          setIsSaving(false);
          return;
        }

        const filters = [{ field: 'id', value: selectedCategory.id }];

        const response = await fetch('https://deadapi.techysumo.it/techyshop/editCategoria', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify({ values, filters }),
        });

        const result = await response.json();

        if (result.result) {
          await db.techyshop_categorie.update(selectedCategory.id, categoryData);
          setToast({ open: true, message: 'Categoria aggiornata con successo!', severity: 'success' });
          handleCloseModal();
        } else {
          throw new Error(result.text || "Errore durante l'aggiornamento della categoria");
        }
      } else {
        // Logica di aggiunta
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addCategoria', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify(categoryData),
        });

        const result = await response.json();

        if (result.result) {
          const newId = result.details.id || result.details;
          await db.techyshop_categorie.add({ id: newId, ...categoryData });
          setToast({ open: true, message: 'Categoria creata con successo!', severity: 'success' });
          handleCloseModal();
        } else {
          throw new Error(result.text || "Errore durante la creazione della categoria");
        }
      }
    } catch (error) {
      log("Errore nel salvataggio della categoria:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa categoria?')) return;

    try {
      const response = await fetch('https://deadapi.techysumo.it/techyshop/delCategoria', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({ id }),
      });

      const result = await response.json();
      if (result.result) {
        await db.techyshop_categorie.delete(id);
        setToast({ open: true, message: 'Categoria eliminata con successo!', severity: 'info' });
      } else {
        throw new Error(result.text || "Errore durante l'eliminazione");
      }
    } catch (error) {
      log("Errore nell'eliminazione della categoria:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    }
  };

  const handleSync = async () => {
    if (categories && categories.length > 0) {
      if (!window.confirm(`Sono presenti ${categories.length} categorie locali. Vuoi resettare la tabella e riscaricarle dal server?`)) {
        return;
      }
    }

    setIsSyncing(true);
    setToast({ open: true, message: 'Sincronizzazione in corso...', severity: 'info' });
    
    try {
      const result = await syncCategories(true);
      setToast({ 
        open: true, 
        message: result.message, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      log("Errore durante la sincronizzazione manuale:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSyncing(false);
    }
  };

  const getParentName = (pId) => {
    if (!pId || pId === 0) return 'Nessuna (Principale)';
    const parent = categories.find(c => c.id === pId);
    return parent ? parent.nome : `ID: ${pId}`;
  };

  const handleToastClose = () => setToast({ ...toast, open: false });

  if (!categories) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
        <Typography sx={{ mt: 2 }}>Caricamento categorie...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}
        open={isSyncing}
      >
        <Loader />
        <Typography variant="h6">Sincronizzazione categorie in corso...</Typography>
      </Backdrop>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/techyshop')} aria-label="back">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ ml: 2, flexGrow: 1 }}>
          Categorie Prodotto
        </Typography>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button 
          variant="outlined" 
          color="secondary" 
          startIcon={<Sync />} 
          onClick={handleSync}
          disabled={isSaving}
        >
          Sincronizza
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<Add />} 
          onClick={() => handleOpenModal()}
          disabled={isSaving}
        >
          Aggiungi Nuova Categoria
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Cerca per nome, ID o slug"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Nome</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Slug</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Genitore</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="center">Visibile</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCategories.map((cat) => (
              <TableRow 
                key={cat.id} 
                hover 
                onClick={() => handleOpenModal(cat)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } 
                }}
              >
                <TableCell sx={{ color: 'white' }}>{cat.id}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{cat.nome}</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{cat.slug}</TableCell>
                <TableCell>
                  <Chip 
                    label={getParentName(cat.categoria_padre_id)} 
                    size="small" 
                    variant="outlined"
                    sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)' }}
                  />
                </TableCell>
                <TableCell align="center">
                  {(cat.visibile == 1 || String(cat.visibile).toLowerCase() === 'true') ? (
                    <CheckCircle style={{ color: '#4dab56' }} fontSize="small" />
                  ) : (
                    <Cancel style={{ color: '#f44336' }} fontSize="small" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={(e) => { e.stopPropagation(); handleOpenModal(cat); }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat.id); }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredCategories.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    Nessuna categoria trovata.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Aggiungi/Modifica Categoria */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} fullScreen>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" color="inherit" onClick={handleCloseModal} aria-label="close" sx={{ mr: 2 }}>
              <Close />
            </IconButton>
            <Typography variant="h6" component="span">
              {selectedCategory ? 'Modifica Categoria' : 'Aggiungi Nuova Categoria'}
            </Typography>
          </Box>
          {selectedCategory && <Chip label={`ID: ${selectedCategory.id}`} size="small" />}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Informazioni Base</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Nome Categoria"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  disabled={isSaving}
                />
                
                <TextField
                  fullWidth
                  label="Slug"
                  value={slug}
                  placeholder="lascia vuoto per generarlo dal nome"
                  onChange={(e) => setSlug(e.target.value)}
                  disabled={isSaving}
                />

                <TextField
                  fullWidth
                  label="Descrizione"
                  value={descrizione}
                  multiline
                  rows={4}
                  onChange={(e) => setDescrizione(e.target.value)}
                  disabled={isSaving}
                />

                <TextField
                  fullWidth
                  label="URL Immagine"
                  value={immagine}
                  onChange={(e) => setImmagine(e.target.value)}
                  disabled={isSaving}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Configurazione</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth disabled={isSaving}>
                  <InputLabel id="parent-label">Categoria Genitore</InputLabel>
                  <Select
                    labelId="parent-label"
                    value={categoriaPadreId}
                    label="Categoria Genitore"
                    onChange={(e) => setCategoriaPadreId(e.target.value)}
                  >
                    <MenuItem value={0}><em>Nessuna (Principale)</em></MenuItem>
                    {categories
                      .filter(c => !selectedCategory || c.id !== selectedCategory.id)
                      .map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.nome}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="number"
                  label="Ordinamento"
                  value={ordinamento}
                  onChange={(e) => setOrdinamento(e.target.value)}
                  disabled={isSaving}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={visibile}
                      onChange={(e) => setVisibile(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Categoria Visibile"
                  disabled={isSaving}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>SEO (Opzionale)</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Meta Title"
                  value={metaTitle}
                  inputProps={{ maxLength: 60 }}
                  helperText={`${metaTitle.length}/60`}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  disabled={isSaving}
                />
                <TextField
                  fullWidth
                  label="Meta Description"
                  value={metaDescription}
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 160 }}
                  helperText={`${metaDescription.length}/160`}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  disabled={isSaving}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} disabled={isSaving}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveCategory} 
            disabled={isSaving || !categoryName.trim()}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TechyShopCategorie;
